import { useState, useCallback } from "react";

import { useAddBinding } from "./useAddBinding";

import type { AddBindingInput } from "app/mutations/__generated__/AddBindingMutation.graphql";
import type { PayloadError } from "relay-runtime";

interface AddBindingsError extends PayloadError {
  accountGroupUUID?: string;
}

interface UseAddBindingsParams {
  /** Array of inputs for the addBinding mutation */
  inputs: {
    input: AddBindingInput;
    connections: string[];
  }[];
  /** Callback when all addBinding mutations have completed */
  onAllCompleted: () => void;
  /** Callback when any addBinding mutation encounters an error */
  onAnyError: (errors: AddBindingsError[] | null) => void;
}

/** Custom hook to await adding multiple bindings in a single operation */
export function useAddBindings(): [
  (params: UseAddBindingsParams) => void,
  boolean,
] {
  const [addBinding, isInFlight] = useAddBinding();
  const [isProcessing, setIsProcessing] = useState(false);

  const addMultipleBindings = useCallback(
    ({ inputs, onAllCompleted, onAnyError }: UseAddBindingsParams) => {
      setIsProcessing(true);

      const promises = inputs.map(
        (params) =>
          new Promise<void>((resolve, reject) => {
            addBinding(params, (_, errors: PayloadError[] | null) => {
              if (errors && errors.length > 0) {
                reject(
                  errors.map((error) => {
                    // Include the accountGroupUUID to identify which mutation failed
                    return {
                      ...error,
                      accountGroupUUID: params.input.accountGroupUUID,
                    };
                  }),
                );
              } else {
                resolve();
              }
            });
          }),
      );

      Promise.allSettled(promises).then((results) => {
        setIsProcessing(false);

        const rejected = results.filter(
          (result): result is PromiseRejectedResult =>
            result.status === "rejected",
        );

        if (rejected.length > 0 && onAnyError) {
          onAnyError(rejected.map((reject) => reject.reason).flat());
        } else if (onAllCompleted) {
          onAllCompleted();
        }
      });
    },
    [addBinding],
  );

  return [addMultipleBindings, isProcessing || isInFlight];
}
